import React, { useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Container,
  Box,
  makeStyles,
  InputAdornment,
  IconButton,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Link,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Visibility, VisibilityOff, Email } from "@material-ui/icons";
import { useAuth } from "../context/ApiKeyContext";
import { useTranslation } from "../hooks/useTranslation";
import LanguageSelector from "./LanguageSelector";

// URL base da API - usa variável de ambiente ou fallback para produção
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(8),
    display: "flex", 
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  paper: {
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: 500,
    margin: "0 auto",
  },
  formField: {
    marginBottom: theme.spacing(3),
    width: "100%",
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  forgotPassword: {
    marginTop: theme.spacing(2),
    textAlign: "center",
  },
  icon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  infoBox: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  dialogContent: {
    minWidth: 400,
    overflow: "auto",
  },
  passwordRequirement: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  termsDialog: {
    minWidth: 600,
    maxHeight: 400,
    overflow: "auto",
  },
  languageSelector: {
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
  },
  paperWithLanguage: {
    position: "relative",
  },
}));

// Componente para o popup de termos
const TermsDialog = ({ open, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation('login');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('terms.title')}</DialogTitle>
      <DialogContent className={classes.termsDialog}>
        <Typography variant="body2" paragraph>
          <strong>{t('terms.header')}</strong>
        </Typography>

        <Typography variant="body2" paragraph>
          {t('terms.intro')}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t('terms.section1.title')}</strong>
          <br />{t('terms.section1.content')}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t('terms.section2.title')}</strong>
          <br />{t('terms.section2.content')}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t('terms.section3.title')}</strong>
          <br />{t('terms.section3.content')}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t('terms.section4.title')}</strong>
          <br />{t('terms.section4.content')}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t('terms.section5.title')}</strong>
          <br />{t('terms.section5.content')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente para o popup de troca de senha
const PasswordChangeDialog = ({
  open,
  onClose,
  onPasswordChanged,
  userEmail,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('login');
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePassword = (password) => {
    const hasMinLength = password.length >= 8;
    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /\d/.test(password);

    return hasMinLength && hasLetters && hasNumbers;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t('passwordChange.allFieldsRequired'));
      return;
    }

    if (!validatePassword(newPassword)) {
      setError(t('passwordChange.passwordValidation'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('passwordChange.passwordsNotMatch'));
      return;
    }

    if (!acceptTerms) {
      setError(t('passwordChange.acceptTermsRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/users/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          email: userEmail,
          current_password: currentPassword,
          new_password: newPassword,
        }).toString(),
      });

      if (response.ok) {
        onPasswordChanged();
        onClose();
      } else {
        const errorData = await response.json();
        setError(
          errorData.detail ||
            "Erro ao alterar senha. Verifique se a senha atual está correta."
        );
      }
    } catch (error) {
      console.error("Erro ao alterar senha:", error);
      setError(t('passwordChange.changePasswordError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
          <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('passwordChange.title')}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label={t('passwordChange.currentPassword')}
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label={t('passwordChange.newPassword')}
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label={t('passwordChange.repeatNewPassword')}
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography className={classes.passwordRequirement}>
              A nova senha deve ter pelo menos 8 caracteres, incluindo letras e
              números.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  name="acceptTerms"
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  {t('acceptTerms')}{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTerms(true);
                    }}
                  >
                    {t('passwordChange.termsAcceptance')}
                  </Link>
                </Typography>
              }
              style={{ marginTop: 16, marginBottom: 8 }}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={!acceptTerms || isSubmitting}
            variant="contained"
          >
            {isSubmitting ? t('passwordChange.changingPassword') : t('passwordChange.changePassword')}
          </Button>
        </DialogActions>
      </Dialog>

      <TermsDialog open={showTerms} onClose={() => setShowTerms(false)} />
    </>
  );
};

const LoginPage = ({ onLoginSuccess }) => {
  const classes = useStyles();
  const { t } = useTranslation('login');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingAuth, setPendingAuth] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);

  const { saveAuthData } = useAuth();

  // Carregar email salvo ao iniciar
  React.useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!email.trim() || !password.trim()) {
      setError(t('emptyFieldsError'));
      setIsLoading(false);
      return;
    }

    // Gerenciar o email lembrado
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          username: email,
          password: password,
        }).toString(),
      });

      if (response.ok) {
        const data = await response.json();

        // Verificar se é necessário trocar a senha
        if (data.force_password_change) {
          setPendingAuth(data);
          setShowPasswordChange(true);
        } else {
          // Salvar dados de autenticação
          saveAuthData(data.token, {
            email,
            name: data.name,
            permissions: data.permissions,
            cells: data.cells,
            company_name: data.company_name,
            tenant_name: data.tenant_name,
          });
          
          // A validação e seleção de célula agora é feita automaticamente
          // no contexto através da função loadUserCell chamada em saveAuthData

          // Chamar a função de callback para indicar login bem-sucedido
          if (onLoginSuccess) {
            onLoginSuccess();
          }
        }
      } else {
        if (response.status === 401) {
          setError(t('invalidCredentials'));
        } else {
          setError(t('loginError'));
        }
      }
    } catch (error) {
      console.error("Erro ao fazer login:", error);
      setError(t('loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError(t('passwordResetRequestError'));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/users/forgot_password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          email: email,
        }).toString(),
      });

      if (response.ok) {
        setForgotPasswordSent(true);
      } else {
        setError(t('passwordResetError'));
      }
    } catch (error) {
      console.error("Erro ao solicitar recuperação de senha:", error);
      setError(t('passwordResetError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChanged = () => {
    // Após trocar a senha, salvar os dados de autenticação pendente
    if (pendingAuth) {
      saveAuthData(pendingAuth.token, {
        email,
        name: pendingAuth.name,
        permissions: pendingAuth.permissions,
        cells: pendingAuth.cells,
        company_name: pendingAuth.company_name,
        tenant_name: pendingAuth.tenant_name,
      });

      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  if (forgotPasswordSent) {
    return (
      <Container component="main" maxWidth="md">
        <div className={classes.root}>          
          <Paper elevation={3} className={`${classes.paper} ${classes.paperWithLanguage}`}>
            {/* Seletor de língua */}
            <div className={classes.languageSelector}>
              <LanguageSelector variant="login" />
            </div>
            <Email className={classes.icon} />
            <Typography component="h1" variant="h5" className={classes.title}>
              {t('emailSent')}
            </Typography>

            <Alert
              severity="success"  
              style={{ width: "100%", marginBottom: 16 }}
            >
              {t('emailSentMessage', { email })}
            </Alert>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => setForgotPasswordSent(false)}
              className={classes.submit}
            >
              {t('backToLogin')}
            </Button>
          </Paper>
        </div>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <div className={classes.root}>        
        <Paper elevation={3} className={`${classes.paper} ${classes.paperWithLanguage}`}>
          {/* Seletor de língua */}
          <div className={classes.languageSelector}>
            <LanguageSelector variant="login" />
          </div>
          <Typography component="h1" variant="h5" className={classes.title}>
            <img
              src="https://elaw.com.br/wp-content/uploads/2024/08/logo_elaw.png"
              alt="eLaw GenAI"
              className={classes.logo}
            />{" "}
            GenAI
          </Typography>

          <Divider style={{ width: "100%", marginBottom: 20 }} />

          {error && (
            <Alert severity="error" style={{ width: "100%", marginBottom: 16 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('email')}
              name="email"
              autoComplete="email"
              autoFocus
              className={classes.formField}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('password')}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              className={classes.formField}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                />
              }
              label={t('rememberEmail')}
              style={{ marginBottom: 16 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={isLoading}
            >
              {isLoading ? t('signingIn') : t('signIn')}
            </Button>

            <div className={classes.forgotPassword}>
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  handleForgotPassword();
                }}
                disabled={isLoading}
              >
                {t('forgotPassword')}
              </Link>
            </div>
          </form>

          <Box className={classes.infoBox}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              {t('dontHaveAccount')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Entre em contato com nossa equipe para criar sua conta.
            </Typography>
          </Box>
        </Paper>

        <Grid container style={{ marginTop: 40 }}>
          <Grid item xs={12}>
            <Typography
              variant="caption"
              color="textSecondary"
              align="center"
              component="p"
            >
              &copy; {new Date().getFullYear()} eLaw GenAI. Todos os direitos
              reservados.
            </Typography>
          </Grid>
        </Grid>
      </div>

      <PasswordChangeDialog
        open={showPasswordChange}
        onClose={() => setShowPasswordChange(false)}
        onPasswordChanged={handlePasswordChanged}
        userEmail={email}
      />
    </Container>
  );
};

export default LoginPage;
